import {flatMap, flatten} from 'lodash';
import React, {FC, ReactNode, useMemo} from 'react';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../../libs/shared/core/src/services/tracking/tracking';
import {DropdownBoxCoordinator} from '../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {DropdownSeparator} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownSeparator';
import {SimpleDropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdown';
import {SimpleDropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdownItem';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {
  findNearestFontFamilyIndex,
  FontFamilyPresetNamesEnum,
  fontFamilyPresets,
  FontFamilyValue,
  formatFontFamily,
} from '../../../common/editor/style/fontFamilyHelpers';
import {fontFamilyLabels} from '../../../common/editor/style/fontFamilyLabels';

/*
 * Props.
 */

interface FormattingFontFamilyProps {
  fontFamily?: FontFamilyValue;
  onFontFamilySelect: (value: FontFamilyValue) => void;
  isDisabled?: boolean;
  isCompact: boolean;
  isInFloatingToolbar: boolean;
}

/*
 * Constants.
 */

const dropdownMaxHeight = 300;

const indeterminateLabel: ReactNode = '—';

const presetGroups: ReadonlyArray<ReadonlyArray<FontFamilyPresetNamesEnum>> = [
  [
    FontFamilyPresetNamesEnum.SANS_SERIF,
    FontFamilyPresetNamesEnum.SERIF,
    FontFamilyPresetNamesEnum.FIXED_WIDTH,
    FontFamilyPresetNamesEnum.WIDE,
  ],
  [
    FontFamilyPresetNamesEnum.ARIAL,
    FontFamilyPresetNamesEnum.HELVETICA,
    FontFamilyPresetNamesEnum.TIMES_NEW_ROMAN,
    FontFamilyPresetNamesEnum.CALIBRI,
    FontFamilyPresetNamesEnum.COURIER_NEW,
    FontFamilyPresetNamesEnum.COMIC_SANS,
    FontFamilyPresetNamesEnum.GEORGIA,
    FontFamilyPresetNamesEnum.TAHOMA,
    FontFamilyPresetNamesEnum.TREBUCHET,
    FontFamilyPresetNamesEnum.VERDANA,
  ],
];

/*
 * Types.
 */

interface FontFamilyOption {
  name: FontFamilyPresetNamesEnum;
  label: ReactNode;
  fontFamily: FontFamilyValue;
}

/*
 * Component.
 */

export const FormattingFontFamily: FC<FormattingFontFamilyProps> = (props) => {
  // Gather the options.
  const possiblePresetGroups = presetGroups;
  const possibleOptionGroups = useMemo(
    () =>
      possiblePresetGroups.map((possiblePresetNames) =>
        possiblePresetNames.map((name) => ({
          name,
          fontFamily: fontFamilyPresets[name],
          label: fontFamilyLabels[name],
        })),
      ),
    [possiblePresetGroups],
  );
  const possibleOptions = useMemo(() => flatten(possibleOptionGroups), [possibleOptionGroups]);
  const possibleFontFamilies = useMemo(
    () => possibleOptions.map((option) => option.fontFamily),
    [possibleOptions],
  );
  const possibleLabels = useMemo(
    () => [indeterminateLabel, ...possibleOptions.map((option) => option.label)],
    [possibleOptions],
  );

  // Map the selected value to the options.
  const selectedFontFamily = props.fontFamily;
  const selectedIndex = useMemo(
    () => (selectedFontFamily ? findNearestFontFamilyIndex(possibleFontFamilies, selectedFontFamily) : -1),
    [possibleFontFamilies, selectedFontFamily],
  );
  const selectedOption = selectedIndex !== -1 ? possibleOptions[selectedIndex] : undefined;
  const selectedLabel = selectedOption?.label || indeterminateLabel;

  const onFontFamilySelect: (option: FontFamilyOption) => void = (option) => {
    props.onFontFamilySelect(option.fontFamily);
    trackInterfaceEvent(InterfaceEventsEnum.SELECT_FONT_FAMILY, {
      font_family: option.name,
    });
  };

  return (
    <DropdownBoxCoordinator
      value={selectedLabel}
      possibleValues={props.isCompact ? undefined : possibleLabels}
      boxStyles={
        (props.isCompact && {
          width: 120,
        }) ||
        undefined
      }
      size={VisualSizesEnum.MEDIUM}
      shouldDisablePadding
      isDisabled={props.isDisabled}
      renderDropdown={(modalProps) =>
        renderDropdown(
          modalProps,
          possibleOptionGroups,
          selectedOption,
          selectedIndex,
          onFontFamilySelect,
          props.isInFloatingToolbar,
        )
      }
      renderTooltip={() => <Trans id="formatting-fontfamily">Font family</Trans>}
      shouldPreventMouseDown
      interactionId="formatting-fontfamily"
    />
  );
};

/*
 * Dropdown.
 */

/* eslint react/no-array-index-key: 0 */
function renderDropdown(
  modalProps: PopoverTransferProps,
  possibleOptionGroups: ReadonlyArray<ReadonlyArray<FontFamilyOption>>,
  selectedOption: FontFamilyOption | undefined,
  selectedIndex: number,
  onFontFamilySelect: (option: FontFamilyOption) => void,
  isInFloatingToolbar: boolean,
) {
  const dropdownItems = flatMap(possibleOptionGroups, (groupOptions, groupIndex) => {
    const groupItems = groupOptions.map((option) => (
      <SimpleDropdownItem
        key={option.name}
        isChecked={option === selectedOption}
        onSelect={() => onFontFamilySelect(option)}
        interactionId={option.name.toLowerCase()}
      >
        {renderDropdownItemContent(option)}
      </SimpleDropdownItem>
    ));

    return groupIndex === 0
      ? groupItems
      : [<DropdownSeparator key={`separator-${groupIndex}`} />, ...groupItems];
  });

  return (
    <SimpleDropdown
      {...modalProps}
      maxHeight={dropdownMaxHeight}
      defaultHighlightedIndex={selectedIndex}
      shouldCenterHighlightedItem
      interactionId="formatting-fontfamily-dropdown"
    >
      {dropdownItems}
    </SimpleDropdown>
  );
}

function renderDropdownItemContent(option: FontFamilyOption): ReactNode {
  const style = {fontFamily: formatFontFamily(option.fontFamily)};
  return <span style={style}>{option.label}</span>;
}
